import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { default as parseISO } from 'date-fns/parseISO';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'wouter';

import * as AppPropTypes from '../../lib/PropTypes';

import { useLabels } from '../../contexts/LabelsContext';
import { useThemeClassName } from '../../contexts/ThemeContext';
import Blocks from '../blocks/Blocks';
import StyledButton from '../buttons/StyledButton';
import GameResults from '../partials/GameResults';
import GameWin from '../partials/GameWin';
import TimerBox from '../partials/TimerBox';

import styles from '../../../styles/games/game-end.module.scss';

const propTypes = {
    game: AppPropTypes.game,
    participation: AppPropTypes.participation,
    isValid: PropTypes.bool,
    blocks: AppPropTypes.blocks,
    withAnswersCount: PropTypes.bool,
    withoutReturnLink: PropTypes.bool,
    withAccountButton: PropTypes.bool,
    onClickReplay: PropTypes.func,
    className: PropTypes.string,
    children: PropTypes.node,
};

const defaultProps = {
    game: null,
    participation: null,
    isValid: null,
    blocks: null,
    withAnswersCount: false,
    withAccountButton: false,
    onClickReplay: null,
    className: null,
    children: null,
};

function DefaultGameEnd({
    game,
    participation,
    isValid,
    blocks,
    onClickReplay,
    withAnswersCount,
    withAccountButton,
    className,
    children,
}) {
    const { gameEndAccountButton = null, gameEndReplayButton = null } = useLabels();

    const route = useUrlGenerator();
    const [, navigate] = useLocation();
    const themeClassName = useThemeClassName(styles);
    const {
        id: gameId,
        theme: gameTheme,
        // type: gameType = null,
        canParticipate = true,
    } = game || {};
    const {
        endWithoutReturnLink = false,
        name: themeName = null,
        gameEndWithAccountButton = false,
    } = gameTheme || {};

    const finalHasAccountButton =
        (withAccountButton || gameEndWithAccountButton) && !canParticipate;

    const { canParticipateAt = null } = useUser();

    const canParticipateAtDate = useMemo(
        () => (canParticipateAt !== null ? parseISO(canParticipateAt) : null),
        [canParticipateAt],
    );

    const [replayDisabled, setReplayDisabled] = useState(false);

    const onReplay = useCallback(() => {
        setReplayDisabled(false);
        navigate(
            route('game', {
                game: gameId,
            }),
        );
    }, [navigate, route, gameId, setReplayDisabled]);

    const finalOnClickReplay = useCallback(
        (e) => {
            e.preventDefault();
            // Make sure you redirect after the page has
            // correctly reloaded with the new participation
            if (onClickReplay !== null) {
                setReplayDisabled(true);
                Promise.resolve(onClickReplay)
                    .then(() => {
                        onReplay();
                    })
                    .catch(() => {
                        onReplay();
                    });
            } else {
                onReplay();
            }
        },
        [onClickReplay, gameId, onReplay, setReplayDisabled],
    );

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            <div className={styles.inner}>
                {!endWithoutReturnLink ? (
                    <div className={styles.top}>
                        <Link href={route('play')} className={styles.link}>
                            <span className={styles.caret}>
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </span>
                            <span>
                                <FormattedMessage
                                    defaultMessage="Return to game selection"
                                    description="Link label"
                                />
                            </span>
                        </Link>
                    </div>
                ) : null}
                <GameResults
                    game={game}
                    isValid={isValid}
                    participation={participation}
                    withAnswersCount={withAnswersCount}
                    className={styles.results}
                >
                    {children}
                </GameResults>
                <GameWin game={game} participation={participation} className={styles.win} />
                {blocks !== null && blocks.length > 0 ? <Blocks blocks={blocks} /> : null}
                {canParticipateAtDate !== null && themeName !== 'coorslight' ? (
                    <TimerBox
                        date={canParticipateAt}
                        started
                        timerClassName={styles.timer}
                        className={styles.timerBox}
                    />
                ) : null}
                <div className={styles.buttons}>
                    {canParticipate && gameEndReplayButton !== false ? (
                        <StyledButton
                            colored
                            className={styles.button}
                            onClick={finalOnClickReplay}
                            href={route('game', {
                                game: gameId,
                            })}
                            disabled={replayDisabled}
                        >
                            {gameEndReplayButton || (
                                <FormattedMessage
                                    defaultMessage="Play again"
                                    description="Button label"
                                />
                            )}
                        </StyledButton>
                    ) : null}
                    {finalHasAccountButton && gameEndAccountButton !== false ? (
                        <StyledButton colored className={styles.button} href={route('account')}>
                            {gameEndAccountButton || (
                                <FormattedMessage
                                    defaultMessage="See my account"
                                    description="Button label"
                                />
                            )}
                        </StyledButton>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

DefaultGameEnd.propTypes = propTypes;
DefaultGameEnd.defaultProps = defaultProps;

export default DefaultGameEnd;
